import { Modal } from 'bootstrap';
import naja from 'naja';

const MODAL_ID = 'ai-consent-modal';
const ATTRIBUTE = 'data-needs-ai-consent';

interface ConsentModalState {
    element: HTMLElement;
    bsModal: Modal;
    checkbox: HTMLInputElement;
    confirmButton: HTMLButtonElement;
    errorAlert: HTMLElement;
    giveUrl: string;
}

function readModalState(): ConsentModalState | null {
    const element = document.getElementById(MODAL_ID);
    if (element === null) {
        return null;
    }

    const checkbox = element.querySelector<HTMLInputElement>('#ai-consent-modal-checkbox');
    const confirmButton = element.querySelector<HTMLButtonElement>('[data-action="ai-consent-confirm"]');
    const errorAlert = element.querySelector<HTMLElement>('[data-role="ai-consent-error"]');
    const giveUrl = element.getAttribute('data-give-url');

    if (checkbox === null || confirmButton === null || errorAlert === null || giveUrl === null || giveUrl === '') {
        return null;
    }

    return {
        element,
        bsModal: Modal.getOrCreateInstance(element, { backdrop: 'static', keyboard: false }),
        checkbox,
        confirmButton,
        errorAlert,
        giveUrl,
    };
}

function hasConsent(modal: ConsentModalState): boolean {
    return modal.element.getAttribute('data-has-consent') === 'true';
}

function openConsentModal(modal: ConsentModalState, naja: any, onConfirmed: () => void): void {
    modal.checkbox.checked = false;
    modal.confirmButton.disabled = true;
    modal.errorAlert.classList.add('d-none');

    const checkboxHandler = (): void => {
        modal.confirmButton.disabled = ! modal.checkbox.checked;
    };

    const confirmHandler = async (): Promise<void> => {
        if (! modal.checkbox.checked) {
            return;
        }
        modal.confirmButton.disabled = true;
        modal.errorAlert.classList.add('d-none');
        try {
            await naja.makeRequest(
                'POST',
                modal.giveUrl,
                null,
                { history: false, unique: false },
            );
            modal.element.setAttribute('data-has-consent', 'true');
            modal.bsModal.hide();
            onConfirmed();
        } catch {
            modal.confirmButton.disabled = false;
            modal.errorAlert.classList.remove('d-none');
        }
    };

    modal.checkbox.addEventListener('change', checkboxHandler);
    modal.confirmButton.addEventListener('click', confirmHandler);

    modal.element.addEventListener('hidden.bs.modal', () => {
        modal.checkbox.removeEventListener('change', checkboxHandler);
        modal.confirmButton.removeEventListener('click', confirmHandler);
    }, { once: true });

    modal.bsModal.show();
}

export function registerAiConsentInterception(snippet: HTMLElement, naja: any): void {
    const modal = readModalState();
    if (modal === null) {
        return;
    }

    snippet.querySelectorAll<HTMLFormElement>(`form[${ATTRIBUTE}]`).forEach(form => {
        form.addEventListener('submit', (event: Event) => {
            if (hasConsent(modal)) {
                return;
            }
            event.preventDefault();
            event.stopImmediatePropagation();
            openConsentModal(modal, naja, () => {
                form.requestSubmit();
            });
        }, true);
    });

    snippet.querySelectorAll<HTMLElement>(`[${ATTRIBUTE}]:not(form)`).forEach(element => {
        element.addEventListener('click', (event: Event) => {
            if (hasConsent(modal)) {
                return;
            }
            event.preventDefault();
            event.stopImmediatePropagation();
            openConsentModal(modal, naja, () => {
                element.click();
            });
        }, true);
    });
}

/**
 * Resolves true when the user has given the consent to AI, asks for it first when not
 *
 * For actions outside of naja snippets, e.g. in Vue components; the page must render the consent dialog.
 */
export function requestAiConsent(): Promise<boolean> {
    const modal = readModalState();

    if (modal === null || hasConsent(modal)) {
        return Promise.resolve(true);
    }

    return new Promise(resolve => {
        let confirmed = false;

        modal.element.addEventListener('hidden.bs.modal', () => resolve(confirmed), { once: true });

        openConsentModal(modal, naja, () => {
            confirmed = true;
        });
    });
}
